import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Box, TextField } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
    ControlledChipMultiSelect,
    ControlledSingleSelect,
    ControlledTextField,
    Loading,
} from '@/src/ui';

import type { SchoolBaseDTO } from '../models/classDTOs';
import type { ClassDTO, ClassFormDTO } from '../models/classDTOs';
import type { ClassFullDTO } from '../models/classDTOs';
import { apiRequest } from '@/src/services';
import { Constants } from '@/src/utils';

interface CreateEditClassModalProps {
    open: boolean;
    onClose: () => void;
    districtId: number;
    schoolId: number;
    classToEdit?: ClassDTO | null;
    setError?: (msg: string) => void;
    onSubmitCallback?: () => void;
}

const statusOptions = [
    { id: 1, name: 'Active' },
    { id: 2, name: 'Inactive' },
    { id: 3, name: 'Deleted' },
];

const practiceChatOptions = [
    { id: 0, name: 'Inherit' },
    { id: 1, name: 'Enable' },
    { id: 2, name: 'Disable' },
];

const CreateEditClassModal: React.FC<CreateEditClassModalProps> = ({
    open,
    onClose,
    districtId,
    schoolId,
    classToEdit,
    setError,
    onSubmitCallback,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setLocalError] = useState('');
    const schema: yup.ObjectSchema<ClassFormDTO> = yup.object({
        districtId: yup.number().min(1, 'District is required').required('District is required'),
        schoolId: yup.number().min(1, 'School is required').required('School is required'),
        className: yup
            .string()
            .required('Class Name is required')
            .max(
                Constants.MAX_CHARS.CLASS_NAME,
                `Class Name cannot exceed ${Constants.MAX_CHARS.CLASS_NAME} characters`
            ),
        schoolYear: yup
            .number()
            .typeError('School Year must be a number')
            .integer()
            .required('School Year is required'),
        status: yup.number().oneOf([1, 2, 3]).required('Status is required'),
        enablePracticeChatOrInherit: yup.number().oneOf([0, 1, 2]).required(),
        gradeLevelMasterIds: yup
            .array()
            .of(yup.number())
            .min(1, 'Select at least one grade')
            .required(),
        teacherIds: yup.array().of(yup.number()).min(0),
        studentIds: yup.array().of(yup.number()).min(0),
    });

    const {
        control,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm<ClassFormDTO>({
        resolver: yupResolver(schema),
        defaultValues: {
            districtId: classToEdit?.districtId ?? districtId ?? 0,
            schoolId: classToEdit?.schoolId ?? schoolId ?? 0,
            className: classToEdit?.className || '',
            schoolYear: classToEdit?.schoolYear || new Date().getFullYear(),
            status: classToEdit?.status || 1,
            enablePracticeChatOrInherit: classToEdit?.enablePracticeChatOrInherit || 0,
            gradeLevelMasterIds: Array.isArray(classToEdit?.gradeLevelMasterIds)
                ? (classToEdit?.gradeLevelMasterIds as number[]).filter(
                      (id): id is number => typeof id === 'number'
                  )
                : [],
            teacherIds: Array.isArray(classToEdit?.teacherIds)
                ? (classToEdit?.teacherIds as number[]).filter(
                      (id): id is number => typeof id === 'number'
                  )
                : [],
            studentIds: Array.isArray(classToEdit?.studentIds)
                ? (classToEdit?.studentIds as number[]).filter(
                      (id): id is number => typeof id === 'number'
                  )
                : [],
        },
    });

    const [districtOptions, setDistrictOptions] = useState([{ id: 0, name: 'Select District' }]);
    const [schoolOptions, setSchoolOptions] = useState([{ id: 0, name: 'Select School' }]);
    const [selectedDistrictId, setSelectedDistrictId] = useState(
        classToEdit?.districtId ? classToEdit.districtId : districtId || 0
    );
    const [selectedSchoolId, setSelectedSchoolId] = useState(
        classToEdit?.schoolId ? classToEdit.schoolId : schoolId || 0
    );

    // Watch grades for multi-select
    const watchedGradeLevelMasterIds = useWatch({ control, name: 'gradeLevelMasterIds' });

    // Teacher and Student options
    const [teacherOptions, setTeacherOptions] = useState<{ id: number; name: string }[]>([]);
    const [studentOptions, setStudentOptions] = useState<{ id: number; name: string }[]>([]);

    const [success, setSuccess] = useState(false);

    const hasLoaded = useRef(false);

    // Fetch teachers and students for ALL selected grades
    useEffect(() => {
        (async function fetchUsers() {
            if (!selectedDistrictId || !selectedSchoolId) {
                setTeacherOptions([]);
                setStudentOptions([]);
                return;
            }
            const grades = watchedGradeLevelMasterIds || [];
            if (!grades.length) {
                setTeacherOptions([]);
                setStudentOptions([]);
                return;
            }
            try {
                const classIdParam = classToEdit?.classId ? classToEdit.classId : 0;
                const gradeParams = grades.map((g) => `gradeLevelMasterIds=${g}`).join('&');
                const res = await apiRequest(
                    'GET',
                    `dbo/classes/classInfo?classId=${classIdParam}&schoolId=${selectedSchoolId}${gradeParams ? '&' + gradeParams : ''}`
                );
                const data: {
                    allStudents?: { studentId: number; studentName: string }[];
                    allTeachers?: { teacherId: number; teacherName: string }[];
                    assignedStudentIds?: number[];
                    assignedTeacherIds?: number[];
                } = res.data || {};

                // Students
                const allStudents = Array.isArray(data.allStudents)
                    ? data.allStudents.map((s: any) => ({
                          id: s.studentId,
                          name: s.studentName,
                      }))
                    : [];
                setStudentOptions(allStudents);

                // Teachers
                const allTeachers = Array.isArray(data.allTeachers)
                    ? data.allTeachers.map((t: any) => ({
                          id: t.teacherId,
                          name: t.teacherName,
                      }))
                    : [];
                setTeacherOptions(allTeachers);

                // If editing, set assigned students/teachers
                if (classToEdit?.classId) {
                    if (Array.isArray(data.assignedStudentIds)) {
                        setValue('studentIds', data.assignedStudentIds);
                    }
                    if (Array.isArray(data.assignedTeacherIds)) {
                        setValue('teacherIds', data.assignedTeacherIds);
                    }
                }
            } catch {
                setTeacherOptions([]);
                setStudentOptions([]);
            }
        })();
    }, [selectedDistrictId, selectedSchoolId, watchedGradeLevelMasterIds, classToEdit, setValue]);

    // Preload all class data if editing
    useEffect(() => {
        if (!classToEdit || !classToEdit.classId) return;

        const fetchFullClass = async () => {
            try {
                const res = await apiRequest('GET', `dbo/classes/${classToEdit.classId}`);
                const data = res.data as ClassFullDTO;

                // Set district and school
                if (data.districtId) setSelectedDistrictId(data.districtId);
                if (data.schoolId) setSelectedSchoolId(data.schoolId);

                // Set form values
                reset({
                    districtId: data.districtId || 0,
                    schoolId: data.schoolId || 0,
                    className: data.className || '',
                    schoolYear: data.schoolYear || new Date().getFullYear(),
                    status: data.status || 1,
                    enablePracticeChatOrInherit: data.enablePracticeChatOrInherit || 0,
                    gradeLevelMasterIds: Array.isArray(data.gradeLevelMasterIds)
                        ? data.gradeLevelMasterIds.filter((id) => typeof id === 'number')
                        : [],
                    teacherIds: Array.isArray(data.assignedTeachers)
                        ? data.assignedTeachers.map((t) => t.id)
                        : [],
                    studentIds: Array.isArray(data.assignedStudents)
                        ? data.assignedStudents.map((s) => s.id)
                        : [],
                });
            } catch {
                if (setError) setError('Failed to load class details for editing');
            } finally {
                hasLoaded.current = true;
            }
        };
        fetchFullClass();
    }, [classToEdit, reset, error, setError]);

    useEffect(() => {
        async function fetchSchools() {
            if (!selectedDistrictId) {
                setSchoolOptions([{ id: 0, name: 'Select School' }]);
                setSelectedSchoolId(0);
                return;
            }
            try {
                const res = await apiRequest('GET', `dbo/schools?districtId=${selectedDistrictId}`);
                const schools = res.data || [];
                const schoolOpts = [
                    { id: 0, name: 'Select School' },
                    ...(schools as SchoolBaseDTO[]).map((s) => ({ id: s.id ?? 0, name: s.name })),
                ];
                setSchoolOptions(schoolOpts);
            } catch {
                setSchoolOptions([{ id: 0, name: 'Select School' }]);
            }
        }
        fetchSchools();
    }, [selectedDistrictId]);

    const [gradeOptions, setGradeOptions] = useState<{ id: number; name: string }[]>([
        { id: 0, name: 'Loading...' },
    ]);

    useEffect(() => {
        async function fetchFilterData() {
            try {
                const res = await apiRequest(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true&getDistrict=true'
                );
                type GradeLevelMasterOption = { id: number; name: string };
                type DistrictOption = { id: number; name: string };
                const data = res.data as {
                    gradeLevelMasterOptions?: GradeLevelMasterOption[];
                    districtOptions?: DistrictOption[];
                };
                // Grades
                const grades = data.gradeLevelMasterOptions || [];
                const newGradeOptions = grades.map((g) => ({
                    id: g.id,
                    name: g.name,
                }));
                setGradeOptions(newGradeOptions);
                // Districts
                const districts = data.districtOptions || [];
                const newDistrictOptions = districts.map((d) => ({
                    id: d.id,
                    name: d.name,
                }));
                setDistrictOptions([{ id: 0, name: 'Select District' }, ...newDistrictOptions]);
            } catch {
                setGradeOptions([]);
                setDistrictOptions([{ id: 0, name: 'Select District' }]);
            } finally {
                hasLoaded.current = true;
            }
        }
        fetchFilterData();
    }, []);

    const onSubmit = async (data: ClassFormDTO) => {
        setLoading(true);
        setLocalError('');
        if (setError) setError('');
        let classId = classToEdit?.classId;
        const gradeLevelMasterIds = (data.gradeLevelMasterIds || []).map((id) => id);
        const teacherIds = (data.teacherIds || []).map((id) => id);
        const studentIds = (data.studentIds || []).map((id) => id);

        const payload = {
            className: data.className,
            schoolId: classToEdit && classToEdit.schoolId ? classToEdit.schoolId : selectedSchoolId,
            schoolYear: data.schoolYear,
            status: data.status,
            enablePracticeChatOrInherit: data.enablePracticeChatOrInherit,
            classId,
            gradeLevelMasterIds,
            teacherIds,
            studentIds,
        };
        try {
            if (classId) {
                const validGradeIds = Array.isArray(data.gradeLevelMasterIds)
                    ? data.gradeLevelMasterIds.filter((id): id is number => typeof id === 'number')
                    : [];
                const validTeacherIds = Array.isArray(data.teacherIds)
                    ? data.teacherIds.filter((id): id is number => typeof id === 'number')
                    : [];
                const validStudentIds = Array.isArray(data.studentIds)
                    ? data.studentIds.filter((id): id is number => typeof id === 'number')
                    : [];
                const PutPayload = {
                    ...payload,
                    GradeLevelMasterIds: validGradeIds,
                    TeacherIds: validTeacherIds,
                    StudentIds: validStudentIds,
                };
                await apiRequest('PUT', `dbo/classes/${classId}`, PutPayload);
            } else {
                const res = await apiRequest('POST', 'dbo/classes', payload);
                if (res?.data && typeof res.data === 'object') {
                    if ('id' in res.data) {
                        classId = (res.data as { id: number }).id;
                    } else if ('classId' in res.data) {
                        classId = (res.data as { classId: number }).classId;
                    }
                }
            }
            setLoading(false);
            setSuccess(true);
            setTimeout(() => {
                setSuccess(false);
                onClose();
                if (onSubmitCallback) onSubmitCallback();
            }, 750);
        } catch {
            setLocalError('Failed to save class, grade, teacher, or student');
            if (setError) setError('Failed to save class, grade, teacher, or student');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    p: 3,
                    bgcolor: 'background.paper',
                    margin: 'auto',
                    mt: 8,
                    width: '80%',
                    maxWidth: 900,
                    maxHeight: '85vh',
                    overflowY: 'auto',
                }}
            >
                <h2>{classToEdit ? 'Edit Class' : 'Create Class'}</h2>
                <br />
                {!hasLoaded.current ? (
                    <Loading message="Loading..." size="lg" />
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Box display="flex" flexDirection="column" gap={3} p={0}>
                            {classToEdit && classToEdit.districtName ? (
                                <TextField
                                    label="District"
                                    value={classToEdit.districtName}
                                    disabled
                                    fullWidth
                                />
                            ) : (
                                <ControlledSingleSelect
                                    name="districtId"
                                    label="District"
                                    options={districtOptions}
                                    control={control}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        setValue('districtId', v);
                                        setSelectedDistrictId(v);
                                    }}
                                    sx={{ minWidth: '100%' }}
                                    error={errors.districtId}
                                />
                            )}
                            {classToEdit && classToEdit.schoolName ? (
                                <TextField
                                    label="School"
                                    value={classToEdit.schoolName}
                                    disabled
                                    fullWidth
                                />
                            ) : (
                                <ControlledSingleSelect
                                    name="schoolId"
                                    label="School"
                                    options={schoolOptions}
                                    control={control}
                                    onChange={(e) => {
                                        const v = Number(e.target.value);
                                        setValue('schoolId', v);
                                        setSelectedSchoolId(v);
                                    }}
                                    sx={{ width: '100%' }}
                                    error={errors.schoolId}
                                />
                            )}
                            <ControlledTextField
                                label="Class Name"
                                name="className"
                                control={control}
                                error={errors.className}
                                maxLength={Constants.MAX_CHARS.CLASS_NAME}
                            />
                            <ControlledTextField
                                label="School Year"
                                name="schoolYear"
                                type="number"
                                control={control}
                                error={errors.schoolYear}
                            />
                            <ControlledSingleSelect
                                name="status"
                                label="Status"
                                options={statusOptions}
                                control={control}
                                onChange={(e) => setValue('status', Number(e.target.value))}
                                sx={{ width: '100%' }}
                                error={errors.status}
                            />
                            <ControlledSingleSelect
                                name="enablePracticeChatOrInherit"
                                label="Practice Chat/Inherit"
                                options={practiceChatOptions}
                                control={control}
                                onChange={(e) =>
                                    setValue('enablePracticeChatOrInherit', Number(e.target.value))
                                }
                                sx={{ width: '100%' }}
                                error={errors.enablePracticeChatOrInherit}
                            />
                            <ControlledChipMultiSelect
                                key="create-edit-class-grades"
                                name="gradeLevelMasterIds"
                                label="Grades"
                                control={control}
                                options={gradeOptions}
                                error={errors.gradeLevelMasterIds}
                                sx={{ width: '100%' }}
                                disabled={loading}
                            />
                            <ControlledChipMultiSelect
                                key="create-edit-class-students"
                                name="studentIds"
                                label="Students"
                                control={control}
                                options={studentOptions}
                                error={errors.studentIds}
                                sx={{ width: '100%' }}
                                disabled={loading}
                            />
                            <ControlledChipMultiSelect
                                key="create-edit-class-teachers"
                                name="teacherIds"
                                label="Teachers"
                                control={control}
                                options={teacherOptions}
                                error={errors.teacherIds}
                                sx={{ width: '100%' }}
                                disabled={loading}
                            />
                            <Box mt={1}>
                                {error && (
                                    <Box color="error.main" mb={0.5}>
                                        {error}
                                    </Box>
                                )}

                                {success && (
                                    <Box color="success.main" mb={0.5}>
                                        Class {classToEdit ? 'updated' : 'created'} successfully!
                                    </Box>
                                )}

                                {loading && <Box color="text.secondary">Processing...</Box>}
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                            <Button
                                onClick={onClose}
                                disabled={loading}
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={loading}
                                variant="contained"
                                color="primary"
                                sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                            >
                                {classToEdit ? 'Save Changes' : 'Create'}
                            </Button>
                        </Box>
                    </form>
                )}
            </Box>
        </Modal>
    );
};

export default CreateEditClassModal;
